import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, User, ShieldCheck, ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { authService } from '../../services/authService';
import { OperadorSesion } from '../../types/auth.types';
import { INSTITUCION_NOMBRE_COMPLETO } from '../../constants';

interface LoginOperadorViewProps {
  onLoginSuccess: (operador: OperadorSesion) => void;
}

export const LoginOperadorView: React.FC<LoginOperadorViewProps> = ({ onLoginSuccess }) => {
  const [usuario, setUsuario] = useState('');
  const [clave, setClave] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuario.trim() || !clave.trim()) {
      setErrorMsg('Por favor, ingresá tu usuario y contraseña.');
      return;
    }

    try {
      setLoading(true);
      setErrorMsg(null);
      const sesion = await authService.login({ usuario: usuario.trim(), clave: clave.trim() });
      onLoginSuccess(sesion);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Credenciales inválidas. Verificá tus datos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex flex-col justify-center items-center px-4 py-12 relative overflow-hidden">
      {/* Luces de fondo decorativas */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-brand-cyan/10 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        {/* Volver al micrositio */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-brand-cyan transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a la portada del Congreso
          </Link>
        </div>

        {/* Tarjeta de Login */}
        <div className="card-blueprint p-8 rounded-2xl border border-brand-cyan/30 shadow-2xl backdrop-blur-xl bg-brand-navy/80">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex p-3.5 rounded-2xl bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30 mb-4 shadow-inner">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-black text-white tracking-tight font-heading">
              Acceso Operativo
            </h1>
            <p className="text-xs font-semibold text-brand-sky uppercase tracking-wider mt-1">
              Acreditación & Puerta
            </p>
            <p className="text-sm text-slate-300 mt-2">
              Ingresá con tus credenciales asignadas de operador o cajero.
            </p>
          </div>

          {/* Alerta de Error */}
          {errorMsg && (
            <div className="mb-6 p-4 rounded-xl bg-red-950/60 border border-red-500/40 text-red-200 text-sm flex items-start gap-3 animate-fadeIn">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Usuario / Legajo
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <User className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  required
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  placeholder="Ej: operador1 / cajero"
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/90 border border-slate-700 focus:border-brand-cyan rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-1 focus:ring-brand-cyan transition-all"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type="password"
                  required
                  value={clave}
                  onChange={(e) => setClave(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/90 border border-slate-700 focus:border-brand-cyan rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-1 focus:ring-brand-cyan transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-brand-cyan to-brand-sky hover:from-brand-cyan/90 hover:to-brand-sky/90 text-brand-dark font-extrabold text-sm tracking-wider uppercase transition-all duration-200 shadow-lg shadow-brand-cyan/20 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                'Iniciar Turno'
              )}
            </button>
          </form>

          {/* Footer Informativo */}
          <div className="mt-8 pt-6 border-t border-slate-800 text-center">
            <p className="text-[11px] text-slate-400 leading-tight">
              {INSTITUCION_NOMBRE_COMPLETO}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

